"use client";

import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Search, X } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

type Option = string | { value: string; label: string };

interface MultiSelectorProps {
  values: string[];
  onValuesChange: (values: string[]) => void;
  options: Option[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export function MultiSelector({
  values,
  onValuesChange,
  options,
  placeholder = "What are you searching for?",
  className,
  disabled,
}: MultiSelectorProps) {
  const [inputValue, setInputValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [shouldStayClosedAfterEsc, setShouldStayClosedAfterEsc] =
    useState(false);
  const isDisabled = Boolean(disabled);

  const normalizedOptions = useMemo(
    () =>
      options.map((option) =>
        typeof option === "string" ? { value: option, label: option } : option,
      ),
    [options],
  );

  const selectedValuesSet = useMemo(() => new Set(values), [values]);
  const normalizedInput = inputValue.trim().toLowerCase();

  const labelByValue = useMemo(
    () => new Map(normalizedOptions.map((option) => [option.value, option.label])),
    [normalizedOptions],
  );

  const filteredOptions = useMemo(
    () =>
      normalizedOptions.filter(
        (option) =>
          !selectedValuesSet.has(option.value) &&
          option.label.toLowerCase().includes(normalizedInput),
      ),
    [normalizedOptions, normalizedInput, selectedValuesSet],
  );

  const findExactMatch = useCallback(
    (term: string, candidateOptions = normalizedOptions) =>
      candidateOptions.find(
        (option) => option.label.toLowerCase() === term.toLowerCase(),
      ),
    [normalizedOptions],
  );

  const handleSelect = useCallback(
    (optionValue: string) => {
      if (selectedValuesSet.has(optionValue)) return;

      onValuesChange([...values, optionValue]);
      setInputValue("");
      setActiveIndex(-1);
      inputRef.current?.focus();
    },
    [selectedValuesSet, onValuesChange, values],
  );

  const handleRemove = useCallback(
    (optionValue: string) => {
      onValuesChange(values.filter((v) => v !== optionValue));
    },
    [onValuesChange, values],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (isDisabled) return;

      if (!isOpen && (event.key === "Enter" || event.key === "ArrowDown")) {
        if (normalizedInput) {
          const exactMatch = findExactMatch(normalizedInput);
          if (exactMatch && !selectedValuesSet.has(exactMatch.value)) {
            event.preventDefault();
            handleSelect(exactMatch.value);
            return;
          }
        }
        setIsOpen(true);
        setShouldStayClosedAfterEsc(false);
        return;
      }

      if (isOpen) {
        switch (event.key) {
          case "ArrowDown":
            event.preventDefault();
            setActiveIndex((prev) =>
              prev < filteredOptions.length - 1 ? prev + 1 : 0,
            );
            break;
          case "ArrowUp":
            event.preventDefault();
            setActiveIndex((prev) =>
              prev > 0 ? prev - 1 : filteredOptions.length - 1,
            );
            break;
          case "Enter": {
            event.preventDefault();

            if (activeIndex >= 0 && filteredOptions[activeIndex]) {
              handleSelect(filteredOptions[activeIndex].value);
            } else if (normalizedInput) {
              const exactMatch = findExactMatch(normalizedInput, filteredOptions);

              if (exactMatch) {
                handleSelect(exactMatch.value);
              } else {
                const globalExactMatch = findExactMatch(normalizedInput);

                if (
                  globalExactMatch &&
                  !selectedValuesSet.has(globalExactMatch.value)
                ) {
                  handleSelect(globalExactMatch.value);
                }
              }
            }
            break;
          }
          case "Escape":
            event.preventDefault();
            setIsOpen(false);
            setShouldStayClosedAfterEsc(true);
            setActiveIndex(-1);
            inputRef.current?.blur();
            break;
          default:
            break;
        }
      }
    },
    [
      activeIndex,
      filteredOptions,
      findExactMatch,
      handleSelect,
      isOpen,
      isDisabled,
      normalizedInput,
      selectedValuesSet,
    ],
  );

  useEffect(() => {
    if (!isOpen || isDisabled) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setActiveIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isDisabled, isOpen]);

  return (
    <div
      ref={containerRef}
      className={cn("w-full overflow-hidden border rounded-md", className)}
    >
      {values.length > 0 && (
        <motion.div className="flex flex-wrap items-center gap-1 px-2 pt-2">
          <AnimatePresence mode="popLayout">
            {values.map((value) => {
              const displayLabel = labelByValue.get(value) || value;
              return (
                <motion.div
                  key={value}
                  layout
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="flex items-center gap-1.5 px-1 py-px text-xs font-medium border rounded-md transition-colors duration-150 max-w-[200px]">
                    <span className="truncate">{displayLabel}</span>
                    <button
                      type="button"
                      disabled={isDisabled}
                      onClick={() => handleRemove(value)}
                      className="text-muted-foreground transition-colors duration-150 flex-shrink-0 cursor-pointer disabled:cursor-not-allowed"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </motion.div>
      )}

      {/* Search Input */}
      <div className="relative">
        <div className="flex items-center px-2 py-3">
          <Search className="h-4 w-4 text-muted-foreground mr-3" />
          <input
            disabled={isDisabled}
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              if (!isDisabled && !shouldStayClosedAfterEsc) {
                setIsOpen(true);
              }
            }}
            onFocus={() => {
              if (!isDisabled && !shouldStayClosedAfterEsc) {
                setIsOpen(true);
              }
            }}
            onClick={() => {
              if (isDisabled) return;
              setShouldStayClosedAfterEsc(false);
              setIsOpen(true);
            }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="flex-1 text-sm placeholder:text-muted-foreground bg-transparent outline-none disabled:opacity-50 disabled:cursor-not-allowed"
          />
          {isOpen && !isDisabled && (
            <kbd className="text-xs text-muted-foreground px-1.5 py-0.5 rounded font-mono">
              Esc
            </kbd>
          )}
        </div>
      </div>

      {/* Dropdown Options */}
      <div
        className={cn(
          "border-t transition-all duration-200 ease-out",
          isOpen && !isDisabled
            ? "max-h-64 opacity-100"
            : "max-h-0 opacity-0 overflow-hidden",
        )}
      >
        <div className="">
          <AnimatePresence mode="popLayout">
            {filteredOptions.length === 0 ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-sm text-muted-foreground text-center py-4"
              >
                {inputValue
                  ? `No matches found for "${inputValue}"`
                  : "All options are selected"}
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex flex-wrap gap-2 max-h-60 overflow-y-auto pt-2 pl-2 pb-2"
              >
                {filteredOptions.map((option, index) => (
                  <motion.div
                    key={option.value}
                    layout
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Badge
                      variant="outline"
                      className={cn(
                        "cursor-pointer border-dashed",
                        activeIndex === index && "ring-2 ring-offset-2",
                      )}
                      onClick={() => handleSelect(option.value)}
                    >
                      {option.label}
                    </Badge>
                  </motion.div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
